import React, { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useApp } from "@/context/AppContext";
import { todayISO } from "@/lib/format";
import { Mail, Phone, MapPin, MessageCircle, Clock } from "lucide-react";

const INTERESTS = ["Living", "Invests", "Luxe", "Property Consultation", "Developer Partnership", "Other"];

export default function Contact() {
  const { addItem, data } = useApp();
  const s = data.settings || {};
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "Living", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!/^[+\d][\d\s-]{7,}$/.test(form.phone)) errs.phone = "Enter a valid phone number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.message.trim()) errs.message = "Please add a message";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await addItem("enquiries", { ...form, createdAt: todayISO(), status: "New" });
      toast.success("Thank you — your enquiry has reached Archedia.");
      setForm({ name: "", phone: "", email: "", interest: "Living", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send right now. Please try again.");
    } finally { setSubmitting(false); }
  };

  const field = (k, label, type = "text") => (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">{label}</label>
      <input
        type={type} value={form[k]} onChange={(e) => set(k, e.target.value)}
        data-testid={`contact-${k}`}
        className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-4 py-3 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--accent-gold)]"
      />
      {errors[k] && <p className="mt-1 text-xs text-red-500" data-testid={`contact-error-${k}`}>{errors[k]}</p>}
    </div>
  );

  return (
    <div className="container-arch pt-32 pb-24">
      <Reveal>
        <div className="overline">Contact</div>
        <h1 className="mt-4 font-serif text-4xl font-light text-[var(--text-primary)] sm:text-5xl">Talk to Archedia</h1>
        <p className="mt-4 max-w-xl text-[var(--text-muted)]">Tell us how we can help. We respond with perspective, not pressure.</p>
      </Reveal>

      <div className="mt-12 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <Reveal>
          <form onSubmit={submit} className="arch-card grid gap-5 p-6 sm:p-8" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              {field("name", "Name")}
              {field("phone", "Phone")}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {field("email", "Email", "email")}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Interest</label>
                <select value={form.interest} onChange={(e) => set("interest", e.target.value)} data-testid="contact-interest" className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-4 py-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]">
                  {INTERESTS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
            </div>
            {field("subject", "Subject")}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">Message</label>
              <textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-4 py-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]" />
              {errors.message && <p className="mt-1 text-xs text-red-500" data-testid="contact-error-message">{errors.message}</p>}
            </div>
            <button type="submit" data-testid="contact-submit" className="btn-gold justify-self-start">Send Enquiry</button>
          </form>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="arch-card h-full p-8">
            <div className="overline">{s.companyName || "Archedia Solutions"}</div>
            <div className="mt-6 space-y-5">
              <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-[var(--accent-gold)]" /><div><div className="text-sm font-medium text-[var(--text-primary)]" data-testid="contact-address">{s.address}</div><div className="text-sm text-[var(--text-muted)]">{s.serviceNote}</div></div></div>
              {s.email && <div className="flex items-start gap-3"><Mail className="mt-0.5 h-5 w-5 text-[var(--accent-gold)]" /><div className="text-sm text-[var(--text-primary)]" data-testid="contact-email-info">{s.email}</div></div>}
              {s.phone && <div className="flex items-start gap-3"><Phone className="mt-0.5 h-5 w-5 text-[var(--accent-gold)]" /><div className="text-sm text-[var(--text-primary)]" data-testid="contact-phone-info">{s.phone}</div></div>}
              {s.whatsapp && <div className="flex items-start gap-3"><MessageCircle className="mt-0.5 h-5 w-5 text-[var(--accent-gold)]" /><div className="text-sm text-[var(--text-primary)]">WhatsApp {s.whatsapp}</div></div>}
              {s.hours && <div className="flex items-start gap-3"><Clock className="mt-0.5 h-5 w-5 text-[var(--accent-gold)]" /><div className="text-sm text-[var(--text-primary)]">{s.hours}</div></div>}
            </div>
            <div className="my-7 hairline" />
            <p className="font-serif text-xl font-light italic text-[var(--text-primary)]">{s.tagline || "Perspective Builds Possibilities."}</p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
